import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FieldProps } from 'formik';
import debounce from 'lodash.debounce';
import { styled } from 'linaria/react';
import { withTheme } from '@shared/lib';
import { PhoneInput } from '@shared/ui/molecules';
import { InputHelperText, InputErrorMessage } from '@shared/ui/atoms';
export interface TextFieldProps extends FieldProps, KitTextFieldProps {
  hint: string;
  className?: string;
  onBlur?: () => void;
  onClick?: () => void;
}
export const PhoneInputField: FC<TextFieldProps> = ({
  field,
  form: {
    touched,
    errors,
    setFieldTouched,
    setFieldValue
  },
  onChange,
  hint,
  className,
  onClear,
  ...rest
}) => {
  const touchedItem = touched[field.name];
  const errorItem = errors[field.name];
  const [hasError, setHasError] = useState<boolean>(Boolean(touchedItem) && Boolean(errorItem));
  const setHasErrorDebounced = useMemo(() => debounce((hasError: boolean) => setHasError(hasError), 100, {
    leading: false,
    trailing: true
  }), [setHasError]);
  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(event);
    } else {
      setFieldValue(field.name, event.target.value);
      setFieldTouched(field.name, true, false);
    }
  }, [field.name, onChange, setFieldTouched, setFieldValue]);
  const handleClear = useCallback((event: React.MouseEvent<HTMLElement>) => {
    if (onClear) {
      onClear(event);
    } else {
      setFieldValue(field.name, '');
    }
  }, [field.name, onClear, setFieldValue]);
  useEffect(() => {
    setHasErrorDebounced(touchedItem && Boolean(errorItem));
  }, [touchedItem, errorItem, setHasErrorDebounced]);
  return <>
      <CustomPhoneInput {...field} {...rest} value={field.value ? field.value : ''} data-testid={field.name} hasError={hasError} onClear={handleClear} onChange={handleChange} />
      {hint && <InputHelperText>{hint}</InputHelperText>}
      <InputErrorMessage name={field.name} />
    </>;
};
const _exp = /*#__PURE__*/() => PhoneInput as FC;
const _exp2 = /*#__PURE__*/() => ({
  theme
}) => theme.colors.text.secondary;
const _exp3 = /*#__PURE__*/() => ({
  hasError,
  theme
}) => hasError ? theme.colors.state.active : theme.colors.text.secondary;
const _exp5 = /*#__PURE__*/() => ({
  theme
}) => theme.font.size.xxs;
const _exp7 = /*#__PURE__*/() => ({
  hasError,
  theme
}) => hasError ? theme.colors.state.active : theme.colors.state.alternativeSecondaryHover;
const _exp8 = /*#__PURE__*/() => ({
  theme
}) => theme.font.family.MTSCompact;
const _exp9 = /*#__PURE__*/() => ({
  theme
}) => theme.colors.bg.tertiary;
const _exp0 = /*#__PURE__*/() => ({
  theme
}) => theme.font.size.xs;
const _exp10 = /*#__PURE__*/() => ({
  theme
}) => theme.colors.text.primary;
const CustomPhoneInput = withTheme(/*#__PURE__*/styled(_exp())({
  name: "CustomPhoneInput",
  class: "co78tzb",
  propsAsIs: true,
  vars: {
    "co78tzb-0": [_exp2()],
    "co78tzb-1": [_exp3()],
    "co78tzb-2": [_exp8()],
    "co78tzb-3": [_exp5(), "px"],
    "co78tzb-4": [_exp10()],
    "co78tzb-5": [_exp7()],
    "co78tzb-6": [_exp9()],
    "co78tzb-7": [_exp0(), "px"]
  }
}));

require("./PhoneInputField.linaria.module.css!=!../../../../node_modules/linaria/node_modules/@linaria/webpack4-loader/lib/outputCssLoader.js?cacheProvider=!./PhoneInputField.tsx");